import { DocumentSource, FOLDER_TOKEN_PREFIX } from "./marvinSources";

/**
 * Search-scope token grammar for the project search endpoint (`?scope=`) and
 * Documents deep links (`app/search_scope.resolve_search_scope` on the
 * server). `CONTRACT_DOCS_SCOPE` and `FOLDER_SCOPE_PREFIX` are the same
 * tokens `shared/marvinSources.ts` uses for the Marvin `documentFilters`
 * grammar — both mean "contract docs" / "this folder's subtree" to the
 * backend — so the literals live there and are re-exported here. The
 * `category:documents` scope is also shared with Marvin; iOS sends the other
 * category filters through Marvin too so both native pickers use one list.
 */
export const CONTRACT_DOCS_SCOPE: string = DocumentSource.ContractDocs;
export const DOCUMENTS_CATEGORY_SCOPE: string = DocumentSource.Documents;
export const PROJECT_MANAGEMENT_SCOPE = "category:project_management";
export const FOLDER_SCOPE_PREFIX = FOLDER_TOKEN_PREFIX;

const FOLDER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface SearchScopeOption {
    /** `undefined` searches the whole project (no `scope` param). */
    token: string | undefined;
    label: string;
}

/**
 * The scopes the picker offers, in menu order. Every token here is one
 * app/search_scope.resolve_search_scope already understands — the picker adds
 * no vocabulary of its own, and a token missing from this list is rejected as
 * a stale deep link by parseSearchScopeParam.
 */
export const SEARCH_SCOPE_OPTIONS: SearchScopeOption[] = [
    { token: undefined, label: "Everything" },
    { token: CONTRACT_DOCS_SCOPE, label: "Drawings · Specs · RFIs" },
    { token: "category:drawing", label: "Sheets" },
    { token: "category:specification", label: "Specifications" },
    { token: "category:schedule", label: "Schedule" },
    { token: PROJECT_MANAGEMENT_SCOPE, label: "RFIs & submittals" },
    { token: DOCUMENTS_CATEGORY_SCOPE, label: "Documents" },
];

const NAMED_SCOPE_LABELS = new Map(
    SEARCH_SCOPE_OPTIONS.filter((option) => option.token).map((option) => [
        option.token as string,
        option.label,
    ]),
);

export interface ParsedSearchScopeParam {
    scope: string | undefined;
    invalid: boolean;
}

/** Validate the public scope forms emitted by the picker and by Documents/Marvin deep links. */
export function parseSearchScopeParam(rawScope: string | null): ParsedSearchScopeParam {
    if (rawScope === null) return { scope: undefined, invalid: false };

    const scope = rawScope.trim().toLowerCase();
    if (NAMED_SCOPE_LABELS.has(scope)) return { scope, invalid: false };

    if (scope.startsWith(FOLDER_SCOPE_PREFIX)) {
        const folderId = scope.slice(FOLDER_SCOPE_PREFIX.length);
        if (FOLDER_ID_PATTERN.test(folderId)) return { scope, invalid: false };
    }

    return { scope: undefined, invalid: true };
}

/** A scope carried in from a `folder:<uuid>` deep link, with a human label for its chip. */
export interface FolderScopeChip {
    token: string;
    label: string;
}

/** Menu label for an active scope — folder scopes only have a name if a deep link supplied one. */
export function searchScopeLabel(scope: string | undefined, folderLabel?: string | null): string {
    if (!scope) return "Everything";
    return NAMED_SCOPE_LABELS.get(scope) ?? folderLabel ?? "This folder";
}
